import React, { useState, useEffect } from "react";
import projectsService from "../../../services/projectsService.js";
import ModalCustomFieldFor from "../CustomFields/ModalCustomFieldFor.js";
import Statuses from "../Components/Statuses.js";
import ProjectCustomFieldEmbed from "./ProjectCustomFieldsEmbed.js";

const ModalProject = ({ isOpen, onClose, editingModal, loadContent, titleSingular, customFields }) => {
    // Datos iniciales de los campos estándar
    const initialFormData = {
        project_name: "",
        description: "",
        started_at: "",
        deadline: "",
        status_id: "",
        active: true,
        custom_fields_groups: [],
    };

    // Estado del formulario y de los valores personalizados agrupados
    const [formData, setFormData] = useState(initialFormData);
    const [customGroupsValues, setCustomGroupsValues] = useState([]); // [{ groupId, values }]
    const [activeGroupModal, setActiveGroupModal] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // Cargar datos al abrir para editar, o reiniciar para crear
    useEffect(() => {
        if (editingModal) {
            // Parsear grupos de campos
            let parsedGroups = [];
            try {
                parsedGroups = JSON.parse(editingModal.custom_fields_groups) || [];
            } catch {}

            // Parsear valores: puede venir como array (nuevo formato) o como objeto (antiguo formato)
            let rawValues;
            try {
                rawValues = JSON.parse(editingModal.custom_fields_data_values);
            } catch {
                rawValues = [];
            }
            let parsedValues = [];
            if (Array.isArray(rawValues)) {
                parsedValues = rawValues;
            } else if (rawValues && typeof rawValues === "object") {
                // Migrar antiguo objeto a array por cada grupo
                parsedValues = parsedGroups.map((groupId) => ({ groupId, values: rawValues }));
            }

            // Ajustar estado de formulario estándar
            setFormData({
                project_name: editingModal.project_name,
                description: editingModal.description,
                started_at: editingModal.started_at ? editingModal.started_at.split("T")[0] : "",
                deadline: editingModal.deadline ? editingModal.deadline.split("T")[0] : "",
                status_id: editingModal.status_id || "",
                active: editingModal.active,
                custom_fields_groups: parsedGroups,
            });

            // Estado de valores personalizados migrado o nuevo
            setCustomGroupsValues(parsedValues);

            // Estado de selección de estatus
            setSelectedStatus({
                id: editingModal.status_id,
                name: editingModal.status,
                color: editingModal.status_color,
            });
        } else {
            // Al crear proyecto nuevo
            setFormData(initialFormData);
            setCustomGroupsValues([]);
            setSelectedStatus(null);
        }
    }, [editingModal]);

    // Cambios en campos estándar
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    };

    // Toggle de selección de grupos personalizados
    const handleToggleGroup = (groupId) => {
        setFormData((prev) => {
            const exists = prev.custom_fields_groups.includes(groupId);
            const newGroups = exists
                ? prev.custom_fields_groups.filter((id) => id !== groupId)
                : [...prev.custom_fields_groups, groupId];
            return { ...prev, custom_fields_groups: newGroups };
        });
        setCustomGroupsValues((prev) => {
            const exists = prev.some((entry) => entry.groupId === groupId);
            if (exists) {
                return prev.filter((entry) => entry.groupId !== groupId);
            }
            return [...prev, { groupId, values: {} }];
        });
    };

    // Actualizar valores del grupo desde el modal interno
    const handleCustomGroupValuesChange = (groupId, values) => {
        setCustomGroupsValues((prev) =>
            prev.map((entry) =>
                entry.groupId === groupId ? { ...entry, values } : entry
            )
        );
    };

    // Selección de estado
    const handleStatusesOptionClick = (status) => {
        setSelectedStatus(status);
        setFormData({ ...formData, status_id: status.id });
    };

    // Envío del formulario
    const handleSubmit = (e) => {
        e.preventDefault();
        const method = editingModal ? "PUT" : "POST";
        const groups = formData.custom_fields_groups;
        const values = customGroupsValues.map(entry => ({
            groupId: entry.groupId,
            values: entry.values
        }));

        const payload = {
            ...formData,
            custom_fields_groups: JSON.stringify(groups),
            custom_fields_data_values: JSON.stringify(values),
        };
        if (method === "PUT") {
            projectsService.setProjects(editingModal.id, payload).then(() => {
                loadContent();
                onClose();
            });
        } else {
            projectsService.createProject(payload).then(() => {
                loadContent();
                onClose();
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <button className="close-button" onClick={onClose}>&times;</button>
                <h2 className="modal-title">{editingModal ? `Editar ${titleSingular}` : `Crear ${titleSingular}`}</h2>

                {/* Modal interno para editar valores de un grupo personalizado */}
                {activeGroupModal && (
                    <ModalCustomFieldFor
                        group={activeGroupModal}
                        values={
                            customGroupsValues.find((entry) => entry.groupId === activeGroupModal.id)
                                ?.values || {}
                        }
                        onChange={(data) => handleCustomGroupValuesChange(activeGroupModal.id, data)}
                        onClose={() => setActiveGroupModal(null)}
                    />
                )}

                <form onSubmit={handleSubmit}>
                    {/* Campos estándar (nombre, descripción, fechas, estatus, activo) */}
                    <div className="form-group">
                        <label>Nombre del {titleSingular}</label>
                        <input type="text" className="form-control" name="project_name" value={formData.project_name} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                        <label>Descripción</label>
                        <textarea className="form-control" name="description" value={formData.description} onChange={handleChange} rows={3} required />
                    </div>
                    <div className="form-group">
                        <label>Fecha de Inicio</label>
                        <input type="date" className="form-control" name="started_at" value={formData.started_at} onChange={handleChange} required />
                    </div>
                    <div className="form-group">
                        <label>Fecha de término</label>
                        <input type="date" className="form-control" name="deadline" value={formData.deadline} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Estado</label>
                        <Statuses handleStatusesOptionClick={handleStatusesOptionClick} selectedStatus={selectedStatus} />
                    </div>

                    <ProjectCustomFieldEmbed
                        customFields={customFields}
                        customGroupsValues={customGroupsValues}
                        setCustomGroupsValues={setCustomGroupsValues}
                    />


                    <div className="form-group">
                        <fieldset>
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" name="active" checked={formData.active} onChange={handleChange} />
                                <label className="form-check-label">Activado</label>
                            </div>
                        </fieldset>
                    </div>
                    <div className="form-group">
                        <label>Campos Personalizados</label>
                        <fieldset>
                            {customFields.map((group) => {
                                const isChecked = formData.custom_fields_groups.includes(group.id);
                                return (
                                    <div className="form-check form-switch" key={group.id}>
                                        <input className="form-check-input" type="checkbox" checked={isChecked} onChange={() => handleToggleGroup(group.id)} id={`switch-${group.id}`} />
                                        <span className={isChecked ? "form-check-label text-primary" : "form-check-label"} role={isChecked ? "button" : undefined} style={{ cursor: isChecked ? "pointer" : "default", marginLeft: 8 }} onClick={(e) => { if (isChecked) { e.preventDefault(); setActiveGroupModal(group); } }}>
                                          {group.group_name}
                                        </span>
                                    </div>
                                );
                            })}
                        </fieldset>
                    </div>
                    <button type="submit" className="btn btn-primary"><i className="bi bi-save me-2" />{editingModal ? "Guardar" : "Crear Proyecto"}</button>
                    <button type="button" className="btn btn-secondary ms-2" onClick={onClose}><i className="bi bi-x-circle me-2" />Cancelar</button>
                </form>
            </div>
        </div>
    );
};

export default ModalProject;
